/**
 * TAB 5 — Complete Ledger (requirement §37 to §45, AC-13 to AC-15).
 *
 * The main table stays compact on purpose (§38) — ten columns, with the full
 * record behind a detail drawer and the movement graph behind VC Trace.
 *
 * Every filter, search, sort and page is served by the backend (§69). Export
 * re-queries the server under the current filters rather than dumping the
 * visible page, so an export always matches what the operator is looking at
 * (§70).
 */

import * as React from "react";
import {
  Box,
  Grid,
  Drawer,
  Typography,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Button,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import {
  KpiCard,
  TreasuryFilters,
  DataTable,
  StatusChip,
  Panel,
  SectionTitle,
  ErrorBar,
  Loading,
  ExportButton,
  exportRows,
  fmtVC,
  fmtDateTime,
  humanise,
  filterInputSx,
  GOLD,
  BORDER,
} from "../components/common";
import {
  getLedger,
  getLedgerFilterOptions,
  getLedgerDetail,
  traceTransaction,
  exportLedger,
} from "../api";

export const LedgerTab = ({ filters, onFiltersChange }) => {
  const [state, setState] = React.useState({ data: [], pagination: null, summary: null });
  const [options, setOptions] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(25);
  const [sort, setSort] = React.useState({ field: "occurredAt", order: "DESC" });
  const [detailTxn, setDetailTxn] = React.useState(null);

  const [advanced, setAdvanced] = React.useState({
    transactionType: "all",
    status: "all",
    accountType: "all",
    accountId: "",
    accountSearch: "",
    search: "",
    roundId: "",
    referenceId: "",
    provider: "all",
    minAmount: "",
    maxAmount: "",
  });

  React.useEffect(() => {
    getLedgerFilterOptions().then(setOptions).catch((err) => setError(err.message));
  }, []);

  // Both text boxes fire on every keystroke and the account search costs a
  // _User lookup per call, so the filters settle for a moment before a request
  // goes out. Without this, typing a name issued one query per character.
  const [settled, setSettled] = React.useState(advanced);
  React.useEffect(() => {
    const t = setTimeout(() => setSettled(advanced), 350);
    return () => clearTimeout(t);
  }, [advanced]);

  const queryParams = React.useMemo(
    () => ({ ...filters, ...settled, page, limit, sortField: sort.field, sortOrder: sort.order }),
    [filters, settled, page, limit, sort]
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getLedger(queryParams);
        if (!cancelled) setState(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryParams]);

  const setAdv = (patch) => {
    setAdvanced((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  // §39 — the recommended compact column set.
  const columns = [
    { key: "occurredAt", label: "Date/Time", sortable: true, render: (r) => fmtDateTime(r.occurredAt) },
    { key: "txnId", label: "Transaction ID", width: 200 },
    {
      key: "transactionType",
      label: "Type",
      sortable: true,
      render: (r) => (
        <Chip
          size="small"
          label={humanise(r.transactionType)}
          sx={{ backgroundColor: "#242426", color: GOLD, fontSize: "0.62rem" }}
        />
      ),
    },
    { key: "from", label: "From", render: (r) => r.from?.name || r.from?.id || "-" },
    { key: "to", label: "To", render: (r) => r.to?.name || r.to?.id || "-" },
    { key: "amount", label: "Amount", align: "right", sortable: true, render: (r) => fmtVC(r.amount) },
    { key: "status", label: "Status", sortable: true, render: (r) => <StatusChip value={r.status} /> },
    { key: "gameOwner", label: "Game Owner", render: (r) => r.gameOwner?.name || "-" },
    { key: "reference", label: "Reference", width: 150 },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (r) => (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setDetailTxn(r.txnId);
          }}
          sx={{ color: GOLD, textTransform: "none", fontSize: "0.7rem" }}
        >
          View
        </Button>
      ),
    },
  ];

  const s = state.summary || {};

  return (
    <Box>
      <TreasuryFilters
        filters={filters}
        onChange={onFiltersChange}
        extra={
          <TextField
            size="small"
            label="Account Search"
            placeholder="Game Owner, Distributor, Agent or Player name"
            value={advanced.accountSearch}
            onChange={(e) => setAdv({ accountSearch: e.target.value })}
            sx={{ ...filterInputSx, minWidth: 340 }}
            helperText={
              state.accountSearch
                ? `${state.accountSearch.matchedAccounts} account(s) matched${
                    state.accountSearch.truncated ? " (showing the first 200)" : ""
                  }`
                : " "
            }
            FormHelperTextProps={{ sx: { color: "#9E9E9E", fontSize: "0.68rem", mx: 0 } }}
          />
        }
      />

      {/* §42 / §43 — server-side filtering and search across the identifier set. */}
      <Accordion
        sx={{ backgroundColor: "#1C1C1E", border: `1px solid ${BORDER}`, mb: 2, borderRadius: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: GOLD }} />}>
          <Typography sx={{ color: "#FFF", fontSize: "0.82rem" }}>
            Filters &amp; Search
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Txn ID, account, player, round, reference"
                value={advanced.search}
                onChange={(e) => setAdv({ search: e.target.value })}
                sx={filterInputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small" sx={filterInputSx}>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  label="Transaction Type"
                  value={advanced.transactionType}
                  onChange={(e) => setAdv({ transactionType: e.target.value })}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {(options?.transactionTypes || []).flatMap((group) => [
                    <ListSubheader key={group.group}>{group.group}</ListSubheader>,
                    ...group.options.map((t) => (
                      <MenuItem key={t} value={t}>
                        {humanise(t)}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small" sx={filterInputSx}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={advanced.status} onChange={(e) => setAdv({ status: e.target.value })}>
                  <MenuItem value="all">All Statuses</MenuItem>
                  {(options?.statuses || []).map((t) => (
                    <MenuItem key={t} value={t}>
                      {humanise(t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small" sx={filterInputSx}>
                <InputLabel>Account Type</InputLabel>
                <Select
                  label="Account Type"
                  value={advanced.accountType}
                  onChange={(e) => setAdv({ accountType: e.target.value })}
                >
                  <MenuItem value="all">All Account Types</MenuItem>
                  {(options?.accountTypes || []).map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small" sx={filterInputSx}>
                <InputLabel>Provider</InputLabel>
                <Select label="Provider" value={advanced.provider} onChange={(e) => setAdv({ provider: e.target.value })}>
                  <MenuItem value="all">All Providers</MenuItem>
                  {(options?.providers || []).map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Account ID" value={advanced.accountId}
                onChange={(e) => setAdv({ accountId: e.target.value })} sx={filterInputSx} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Round ID" value={advanced.roundId}
                onChange={(e) => setAdv({ roundId: e.target.value })} sx={filterInputSx} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Reference ID" value={advanced.referenceId}
                onChange={(e) => setAdv({ referenceId: e.target.value })} sx={filterInputSx} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" type="number" label="Min Amount" value={advanced.minAmount}
                onChange={(e) => setAdv({ minAmount: e.target.value })} sx={filterInputSx} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" type="number" label="Max Amount" value={advanced.maxAmount}
                onChange={(e) => setAdv({ maxAmount: e.target.value })} sx={filterInputSx} />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                size="small"
                onClick={() =>
                  setAdv({
                    transactionType: "all", status: "all", accountType: "all", accountId: "",
                    search: "", roundId: "", referenceId: "", provider: "all",
                    minAmount: "", maxAmount: "",
                  })
                }
                sx={{ color: "#9E9E9E", textTransform: "none" }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <ErrorBar error={error} onClose={() => setError("")} />

      {/* Eight cards: md={1.5} is not a valid MUI grid width. A flex row
          divides the width evenly and wraps instead of overflowing. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: "1 1 130px", minWidth: 130 }}><KpiCard dense label="GENERATED" value={fmtVC(s.generated)} /></Box>
        <Box sx={{ flex: "1 1 130px", minWidth: 130 }}><KpiCard dense label="ISSUED" value={fmtVC(s.issued)} /></Box>
        <Box sx={{ flex: "1 1 130px", minWidth: 130 }}><KpiCard dense label="RETURNED" value={fmtVC(s.returned)} /></Box>
        <Box sx={{ flex: "1 1 130px", minWidth: 130 }}><KpiCard dense label="TRANSFERS" value={fmtVC(s.transfers)} /></Box>
        <Box sx={{ flex: "1 1 130px", minWidth: 130 }}><KpiCard dense label="BETS" value={fmtVC(s.bets)} /></Box>
        <Box sx={{ flex: "1 1 130px", minWidth: 130 }}><KpiCard dense label="WINS" value={fmtVC(s.wins)} /></Box>
        <Box sx={{ flex: "1 1 130px", minWidth: 130 }}><KpiCard dense label="BONUSES" value={fmtVC(s.bonuses)} /></Box>
        <Box sx={{ flex: "1 1 130px", minWidth: 130 }}><KpiCard dense label="REDEMPTIONS" value={fmtVC(s.redemptions)} /></Box>
        <Box sx={{ flex: "1 1 130px", minWidth: 130 }}><KpiCard dense label="REVERSALS" value={fmtVC(s.reversals)} /></Box>
      </Box>

      <SectionTitle
        action={
          <ExportButton
            disabled={!state.pagination?.total}
            label="Export Ledger"
            onExport={async (format) => {
              // Re-query the server so the export covers every filtered row,
              // not just the page on screen.
              const result = await exportLedger(queryParams);
              exportRows(result.data, "house-treasury-ledger", format);
            }}
          />
        }
      >
        Complete Ledger
      </SectionTitle>

      <DataTable
        dense
        columns={columns}
        rows={state.data}
        loading={loading}
        pagination={state.pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        sortField={sort.field}
        sortOrder={sort.order}
        onSortChange={(field, order) => setSort({ field, order })}
        onRowClick={(r) => setDetailTxn(r.txnId)}
        emptyMessage="No ledger entries match the current filters."
      />

      <LedgerDetailDrawer txnId={detailTxn} onClose={() => setDetailTxn(null)} />
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Detail drawer (§40) + VC trace (§44)
// ═════════════════════════════════════════════════════════════════════════════

const LedgerDetailDrawer = ({ txnId, onClose }) => {
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [trace, setTrace] = React.useState(null);
  const [tracing, setTracing] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!txnId) {
      setDetail(null);
      setTrace(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setTrace(null);
      setError("");
      try {
        const result = await getLedgerDetail({ txnId });
        if (!cancelled) setDetail(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [txnId]);

  const runTrace = async () => {
    setTracing(true);
    try {
      const result = await traceTransaction({ txnId });
      setTrace(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setTracing(false);
    }
  };

  const t = detail?.transaction || {};

  return (
    <Drawer
      anchor="right"
      open={!!txnId}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 620 }, backgroundColor: "#141416", p: 3 } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ color: "#FFF", fontSize: "1rem", fontWeight: 700 }}>
          Transaction Detail
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#9E9E9E" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <ErrorBar error={error} onClose={() => setError("")} />

      {loading ? (
        <Loading />
      ) : detail ? (
        <Box>
          <DetailBlock title="Transaction Information">
            <Row label="Transaction ID" value={t.txnId} />
            <Row label="Type" value={humanise(t.transactionType)} />
            <Row label="Status" value={<StatusChip value={t.status} />} />
            <Row label="Amount" value={`${fmtVC(t.amount)} VC`} />
            <Row label="Occurred At" value={fmtDateTime(t.occurredAt)} />
            <Row label="Completed At" value={fmtDateTime(t.completedAt)} />
            <Row label="Reference ID" value={t.referenceId} />
            <Row label="Remarks" value={t.remarks} />
            <Row label="Source Record" value={`${t.sourceClass || "-"} / ${t.sourceObjectId || "-"}`} />
            <Row label="Idempotency Key" value={t.idempotencyKey} />
          </DetailBlock>

          <DetailBlock title="Source">
            <Row label="Account ID" value={detail.source?.accountId} />
            <Row label="Account Type" value={detail.source?.accountType} />
            <Row label="Name" value={detail.source?.accountName} />
            <Row label="Balance Before" value={detail.source?.balanceBefore == null ? "—" : fmtVC(detail.source.balanceBefore)} />
            <Row label="Balance After" value={detail.source?.balanceAfter == null ? "—" : fmtVC(detail.source.balanceAfter)} />
          </DetailBlock>

          <DetailBlock title="Destination">
            <Row label="Account ID" value={detail.destination?.accountId} />
            <Row label="Account Type" value={detail.destination?.accountType} />
            <Row label="Name" value={detail.destination?.accountName} />
            <Row label="Balance Before" value={detail.destination?.balanceBefore == null ? "—" : fmtVC(detail.destination.balanceBefore)} />
            <Row label="Balance After" value={detail.destination?.balanceAfter == null ? "—" : fmtVC(detail.destination.balanceAfter)} />
          </DetailBlock>

          <DetailBlock title="Hierarchy">
            <Row label="Game Owner" value={detail.hierarchy?.gameOwnerName || detail.hierarchy?.gameOwnerId} />
            <Row label="Master Distributor" value={detail.hierarchy?.masterDistributorId} />
            <Row label="Distributor" value={detail.hierarchy?.distributorId} />
            <Row label="Sub-Distributor" value={detail.hierarchy?.subDistributorId} />
            <Row label="Agent" value={detail.hierarchy?.agentId} />
            <Row label="Player" value={detail.hierarchy?.playerId} />
          </DetailBlock>

          {detail.gameplay && (
            <DetailBlock title="Gameplay">
              <Row label="Game" value={detail.gameplay.gameName} />
              <Row label="Game ID" value={detail.gameplay.gameId} />
              <Row label="Provider" value={detail.gameplay.provider} />
              <Row label="Round ID" value={detail.gameplay.roundId} />
              <Row label="Bet ID" value={detail.gameplay.betId} />
              <Row label="Spin ID" value={detail.gameplay.spinId} />
            </DetailBlock>
          )}

          {detail.bonus && (
            <DetailBlock title="Bonus">
              <Row label="Bonus ID" value={detail.bonus.bonusId} />
              <Row label="Bonus Type" value={humanise(detail.bonus.bonusType)} />
              <Row label="Reward Type" value={detail.bonus.rewardType} />
              <Row label="Gross Reward" value={detail.bonus.grossRewardAmount} />
              <Row label="Multiplier" value={detail.bonus.multiplier} />
              <Row label="Eligibility Reference" value={detail.bonus.eligibilityReference} />
            </DetailBlock>
          )}

          <DetailBlock title="Ledger Entries">
            {(detail.ledger || []).map((leg) => (
              <Box key={leg.ledgerId} sx={{ mb: 1, pb: 1, borderBottom: `1px solid ${BORDER}` }}>
                <Row label="Ledger ID" value={leg.ledgerId} />
                <Row label="Account" value={`${leg.accountName || leg.accountId} (${leg.accountType})`} />
                <Row label="Debit" value={leg.debit ? fmtVC(leg.debit) : "—"} />
                <Row label="Credit" value={leg.credit ? fmtVC(leg.credit) : "—"} />
              </Box>
            ))}
          </DetailBlock>

          <DetailBlock title="Audit">
            <Row label="Created By" value={detail.audit?.createdByName || detail.audit?.createdBy} />
            <Row label="Recorded At" value={fmtDateTime(detail.audit?.timestamp)} />
            <Row label="Reversal Of" value={detail.audit?.reversalOf} />
            <Row label="Reversed By" value={detail.audit?.reversedByTxnId} />
          </DetailBlock>

          {detail.related?.length > 0 && (
            <DetailBlock title="Related Transactions">
              {detail.related.map((r) => (
                <Box key={r.txnId} sx={{ mb: 0.5 }}>
                  <Typography sx={{ color: "#BDBDBD", fontSize: "0.72rem" }}>
                    {humanise(r.transactionType)} · {fmtVC(r.amount)} VC · {r.txnId}
                  </Typography>
                </Box>
              ))}
            </DetailBlock>
          )}

          <Divider sx={{ my: 2, borderColor: BORDER }} />

          <Button
            fullWidth
            variant="outlined"
            startIcon={<AccountTreeIcon />}
            onClick={runTrace}
            disabled={tracing}
            sx={{ color: GOLD, borderColor: BORDER, textTransform: "none" }}
          >
            {tracing ? "Tracing..." : "Trace VC Movement"}
          </Button>

          {trace && (
            <Box sx={{ mt: 2 }}>
              <DetailBlock title="Hierarchy Path">
                {trace.hierarchyPath.map((node, i) => (
                  <Typography key={`${node.accountId}-${i}`} sx={{ color: "#BDBDBD", fontSize: "0.72rem" }}>
                    {"→ ".repeat(i > 0 ? 1 : 0)}
                    {node.level}: {node.name || node.accountId}
                  </Typography>
                ))}
              </DetailBlock>

              <DetailBlock title={`Upstream (${trace.upstream.length})`}>
                {trace.upstream.length === 0 ? (
                  <Typography sx={{ color: "#666", fontSize: "0.72rem" }}>
                    This movement originates at the House Treasury.
                  </Typography>
                ) : (
                  trace.upstream.map((n, i) => (
                    <Typography key={`${n.txnId}-${i}`} sx={{ color: "#BDBDBD", fontSize: "0.7rem" }}>
                      {"  ".repeat(n.depth)}
                      {humanise(n.transactionType)} · {fmtVC(n.amount)} · {n.from?.name} → {n.to?.name}
                    </Typography>
                  ))
                )}
              </DetailBlock>

              <DetailBlock title={`Downstream (${trace.downstream.length})`}>
                {trace.downstream.length === 0 ? (
                  <Typography sx={{ color: "#666", fontSize: "0.72rem" }}>
                    No onward movement recorded from the destination.
                  </Typography>
                ) : (
                  trace.downstream.map((n, i) => (
                    <Typography key={`${n.txnId}-${i}`} sx={{ color: "#BDBDBD", fontSize: "0.7rem" }}>
                      {"  ".repeat(n.depth)}
                      {humanise(n.transactionType)} · {fmtVC(n.amount)} · {n.from?.name} → {n.to?.name}
                    </Typography>
                  ))
                )}
              </DetailBlock>

              <Typography sx={{ color: "#666", fontSize: "0.65rem" }}>
                {trace.limits?.note}
              </Typography>
            </Box>
          )}
        </Box>
      ) : null}
    </Drawer>
  );
};

const DetailBlock = ({ title, children }) => (
  <Panel sx={{ mb: 2, p: 1.5 }}>
    <Typography sx={{ color: GOLD, fontSize: "0.72rem", fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Panel>
);

const Row = ({ label, value }) =>
  value === undefined || value === null || value === "" ? null : (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.35 }}>
      <Typography sx={{ color: "#9E9E9E", fontSize: "0.7rem" }}>{label}</Typography>
      <Typography
        component="div"
        sx={{ color: "#FFF", fontSize: "0.72rem", textAlign: "right", wordBreak: "break-all" }}
      >
        {value}
      </Typography>
    </Box>
  );

export default LedgerTab;
